import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, MenuItem } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FormProvider, useForm } from 'react-hook-form';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import NgpTextField from '../../form/NgpTextField';
import NgpPasswordField from '../../form/NgpPasswordField';
import { AUTO_COMPLETE_DEFAULT, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';

import { CredentialTypes, SNMPV3_PRIVACY_PROTOCOLS, SNMPV3_PROTOCOLS } from './PollingConstants';
import { clearCredPass, formatCredential } from './CredentialHelper';
import { HTTP_STATUS_400, useDataService } from '../../../services/ApiDataService';

const useStyles = makeStyles((theme) => ({
    validation: {
        padding: theme.spacing(2),
        color: 'red',
    },
}));

const validationUrl = '/ngp/v2/polling-config/validate-credential';
const formId = 'credential-form';
const updateTitleText = t`Edit Credential`;
const createTitleText = t`Create Credential`;
const PASSWORD_REPLACEMENT = 'changeme';

const CREDENTIAL_ERROR_MESSAGES = {
    'polling-config.credential.uniqueDescription': t`A credential with this name already exists`,
    'polling-config.credential.uniqueSNMPv2': t`The community string exists, please add a new one.`,
    'polling-config.credential.uniqueSNMPv3': t`The combination of user name, authentication protocol, authentication password, privacy protocol and privacy password already exists, please add a new one.`,
    'polling-config.credential.uniqueWinRM': t`The combination of user name and password exists, please add a new one.`,
    'polling-config.credential.uniqueREST': t`The combination of user name and password exists, please add a new one.`,
    'polling-config.credential.invalidUsername': t`A username is required, please add a nonempty username string`,
};

function resolveCredentialErrorMessage(errorKey) {
    return CREDENTIAL_ERROR_MESSAGES[errorKey] || t`An error occurred while validating the credential`;
}

export default function CredentialDialog({
    showDialog,
    setShowDialog,
    credential,
    originalCredential,
    isNew,
    isUnsaved,
    credentialTypes,
    credentials,
}) {
    const classes = useStyles();
    const [serverError, setServerError] = useState(null);
    const dataService = useDataService();

    const checkLocalError = (checkedCred) => {
        const duplicate = _.some(credentials, (curCred) => {
            if (curCred === checkedCred) return false;
            if (curCred.description === checkedCred.description) {
                setServerError(resolveCredentialErrorMessage('polling-config.credential.uniqueDescription'));
                return true;
            }
            return false;
        });
        return duplicate;
    };
    const dialogMethods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: {},
        mode: 'onChange',
    });
    const { reset, setValue, getValues } = dialogMethods;
    const prevTypeRef = useRef(null);

    useEffect(() => {
        if (showDialog) {
            reset(credential);
            setServerError(null);
            prevTypeRef.current = credential?.type || null;
        }
    }, [showDialog, credential, reset]);

    const {
        watch,
        handleSubmit,
        formState: { dirtyFields },
    } = dialogMethods;

    // Subscribe to all fields via watch() and destructure to primitives — gives stable React dep comparison.
    const { type, description, userName, password, protocol, privacyProtocol, privacyPassword, context, subType } =
        watch();

    // Mirror portal/ui ng-click="dialog.onClearErrors()" on every input/select:
    // clear the server-side validation message whenever the user edits any field.
    useEffect(() => {
        if (!showDialog) return;
        setServerError(null);
    }, [
        showDialog,
        type,
        description,
        userName,
        password,
        protocol,
        privacyProtocol,
        privacyPassword,
        context,
        subType,
    ]);
    const originalType = originalCredential?.type || null;
    const originalProtocol = originalCredential?.protocol || 'None';
    const originalPrivacyProtocol = originalCredential?.privacyProtocol || 'None';

    const typeChangedDifferent = useCallback(
        ({ nextType, passwordDirty, privacyPasswordDirty }) => {
            if (_.includes(['SNMP v3', 'SNMP v2c', 'WinRM', 'REST'], nextType) && !passwordDirty) {
                setValue('password', null, { shouldDirty: false, shouldValidate: true });
            }

            if (nextType === 'SNMP v3' && !privacyPasswordDirty) {
                setValue('privacyPassword', null, { shouldDirty: false, shouldValidate: true });
            }
        },
        [setValue]
    );

    const isTypeChangedPassword = useCallback(({ nextType, passwordDirty }) => {
        return _.includes(['SNMP v2c', 'WinRM', 'REST'], nextType) && !passwordDirty;
    }, []);

    const typeChangedSame = useCallback(
        ({ nextType, passwordDirty, privacyPasswordDirty }) => {
            if (isTypeChangedPassword({ nextType, passwordDirty })) {
                setValue('password', PASSWORD_REPLACEMENT, {
                    shouldDirty: false,
                    shouldValidate: true,
                });
            }

            if (nextType === 'SNMP v3' && !passwordDirty) {
                setValue('password', originalProtocol !== 'None' ? PASSWORD_REPLACEMENT : '', {
                    shouldDirty: false,
                    shouldValidate: true,
                });
            }

            if (nextType === 'SNMP v3' && !privacyPasswordDirty) {
                setValue('privacyPassword', originalPrivacyProtocol !== 'None' ? PASSWORD_REPLACEMENT : '', {
                    shouldDirty: false,
                    shouldValidate: true,
                });
            }
        },
        [isTypeChangedPassword, originalPrivacyProtocol, originalProtocol, setValue]
    );

    const applyCredentialDefaults = useCallback(
        (nextType) => {
            if (nextType === 'REST' && _.isEmpty(getValues('subType'))) {
                setValue('subType', 'RuckusVSZ', { shouldDirty: false });
            }
        },
        [getValues, setValue]
    );

    useEffect(() => {
        if (!showDialog || !type) return;

        const prevType = prevTypeRef.current;
        if (!prevType || prevType === type) {
            prevTypeRef.current = type;
            return;
        }

        const passwordDirty = !!dirtyFields.password;
        const privacyPasswordDirty = !!dirtyFields.privacyPassword;

        if (!isNew && !isUnsaved && originalCredential) {
            if (type !== originalType) {
                typeChangedDifferent({
                    nextType: type,
                    passwordDirty,
                    privacyPasswordDirty,
                });
            } else {
                typeChangedSame({
                    nextType: type,
                    passwordDirty,
                    privacyPasswordDirty,
                });
            }
        }

        applyCredentialDefaults(type);

        prevTypeRef.current = type;
    }, [
        showDialog,
        type,
        isNew,
        isUnsaved,
        originalCredential,
        originalType,
        dirtyFields,
        applyCredentialDefaults,
        typeChangedDifferent,
        typeChangedSame,
    ]);

    const isPasswordRequired = (fieldName) => {
        if (isNew || isUnsaved) return true;
        if (!originalCredential) return true;
        if (type !== originalType) return true;
        if (fieldName === 'password' && type === 'SNMP v3') {
            return originalProtocol === 'None' && protocol !== 'None';
        }
        if (fieldName === 'privacyPassword') {
            return originalPrivacyProtocol === 'None' && privacyProtocol !== 'None';
        }
        return false;
    };

    const handleFormSubmit = async (formValues) => {
        if (checkLocalError(formValues)) return;
        const formattedCred = formatCredential(formValues, { isNew, isUnsaved, dirtyFields });
        try {
            await dataService.post(validationUrl, {
                credential: formattedCred,
                credentials,
            });
        } catch (error) {
            if (HTTP_STATUS_400 === error.getStatus?.() || HTTP_STATUS_400 === _.get(error, 'responseBody.httpCode')) {
                const errorKey = _.get(error, 'responseBody.errorKey');
                setServerError(resolveCredentialErrorMessage(errorKey));
            } else {
                setServerError(resolveCredentialErrorMessage());
            }
            return;
        }
        setShowDialog(clearCredPass(formattedCred));
    };

    return (
        <NgpTwoButtonDialog
            title={isNew ? createTitleText : updateTitleText}
            confirmButtonText={<Trans>OK</Trans>}
            cancelButtonText={<Trans>Cancel</Trans>}
            open={showDialog}
            setOpen={setShowDialog}
            onConfirm={handleSubmit(handleFormSubmit)}
            confirmButtonProps={{
                variant: 'indeterminate',
                color: 'inherit',
            }}
            disableBackdropClick
            callerWillCloseOnConfirm
        >
            <Box width={500}>
                <FormProvider {...dialogMethods}>
                    <NgpForm formId={formId} initialized showSave={false} submitFunction={handleFormSubmit}>
                        {serverError && (
                            <div className={classes.validation}>
                                <Trans>{serverError}</Trans>
                            </div>
                        )}
                        <FormSection>
                            <NgpSelect fieldName={'type'} label={<Trans>Type</Trans>} fullWidth required>
                                {_.map(credentialTypes, (_type) => (
                                    <MenuItem key={_type} value={_type}>
                                        {CredentialTypes[_type]}
                                    </MenuItem>
                                ))}
                            </NgpSelect>
                            <NgpTextField
                                fieldName={'description'}
                                label={<Trans>Credential Name</Trans>}
                                required
                                fullWidth
                                showCharCounter
                                inputProps={{ maxLength: 45 }}
                            />
                            {type === 'SNMP v2c' && (
                                <NgpPasswordField
                                    fieldName={'password'}
                                    required={isPasswordRequired('password')}
                                    label={<Trans>Community String</Trans>}
                                    inputProps={{
                                        maxLength: 255,
                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                    }}
                                    fullWidth
                                    showCharCounter
                                    togglePasswordVisibility
                                />
                            )}
                            {type === 'WinRM' && (
                                <NgpTextField
                                    fieldName={'userName'}
                                    label={<Trans>User Name</Trans>}
                                    required
                                    fullWidth
                                    showCharCounter
                                    inputProps={{ maxLength: 45, autoComplete: AUTO_COMPLETE_DEFAULT }}
                                />
                            )}
                            {type === 'WinRM' && (
                                <NgpPasswordField
                                    fieldName={'password'}
                                    required={isPasswordRequired('password')}
                                    label={<Trans>Password</Trans>}
                                    inputProps={{
                                        maxLength: 255,
                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                    }}
                                    fullWidth
                                    showCharCounter
                                    togglePasswordVisibility
                                />
                            )}
                            {type === 'SNMP v3' && (
                                <>
                                    <NgpTextField
                                        fieldName={'userName'}
                                        label={<Trans>User Name</Trans>}
                                        required
                                        fullWidth
                                        showCharCounter
                                        inputProps={{ maxLength: 45, autoComplete: AUTO_COMPLETE_DEFAULT }}
                                    />
                                    <NgpTextField
                                        fieldName={'context'}
                                        label={<Trans>Context</Trans>}
                                        fullWidth
                                        showCharCounter
                                        inputProps={{ maxLength: 45 }}
                                    />
                                    <NgpSelect
                                        fieldName={'protocol'}
                                        label={<Trans>Authentication Protocol</Trans>}
                                        fullWidth
                                        required
                                    >
                                        {_.map(SNMPV3_PROTOCOLS, (_protocol) => (
                                            <MenuItem key={_protocol} value={_protocol}>
                                                {_protocol}
                                            </MenuItem>
                                        ))}
                                    </NgpSelect>
                                    {protocol !== 'None' && (
                                        <>
                                            <NgpPasswordField
                                                fieldName={'password'}
                                                label={<Trans>Authentication Password</Trans>}
                                                required={isPasswordRequired('password')}
                                                inputProps={{
                                                    maxLength: 255,
                                                    autoComplete: AUTO_COMPLETE_DEFAULT,
                                                }}
                                                fullWidth
                                                showCharCounter
                                                togglePasswordVisibility
                                            />
                                            <NgpSelect
                                                fieldName={'privacyProtocol'}
                                                label={<Trans>Privacy Protocol</Trans>}
                                                fullWidth
                                                required
                                            >
                                                {_.map(SNMPV3_PRIVACY_PROTOCOLS, (_protocol) => (
                                                    <MenuItem key={_protocol} value={_protocol}>
                                                        {_protocol}
                                                    </MenuItem>
                                                ))}
                                            </NgpSelect>
                                            {privacyProtocol !== 'None' && (
                                                <NgpPasswordField
                                                    fieldName={'privacyPassword'}
                                                    required={isPasswordRequired('privacyPassword')}
                                                    label={<Trans>Privacy Password</Trans>}
                                                    inputProps={{
                                                        maxLength: 255,
                                                        autoComplete: AUTO_COMPLETE_DEFAULT,
                                                    }}
                                                    fullWidth
                                                    showCharCounter
                                                    togglePasswordVisibility
                                                />
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                            {type === 'REST' && (
                                <>
                                    <NgpSelect fieldName={'subType'} label={<Trans>API Type</Trans>} fullWidth required>
                                        {_.map(['RuckusVSZ'], (_subType) => (
                                            <MenuItem key={_subType} value={_subType}>
                                                {'Ruckus® Virtual SmartZone'}
                                            </MenuItem>
                                        ))}
                                    </NgpSelect>
                                    <NgpTextField
                                        fieldName={'userName'}
                                        label={<Trans>User Name</Trans>}
                                        required
                                        fullWidth
                                        showCharCounter
                                        inputProps={{ maxLength: 45, autoComplete: AUTO_COMPLETE_DEFAULT }}
                                    />
                                    <NgpPasswordField
                                        fieldName={'password'}
                                        required={isPasswordRequired('password')}
                                        label={<Trans>Password</Trans>}
                                        inputProps={{
                                            maxLength: 255,
                                            autoComplete: AUTO_COMPLETE_DEFAULT,
                                        }}
                                        fullWidth
                                        showCharCounter
                                        togglePasswordVisibility
                                    />
                                </>
                            )}
                        </FormSection>
                    </NgpForm>
                </FormProvider>
            </Box>
        </NgpTwoButtonDialog>
    );
}
